import type { DeliverableStatus } from "@dream/config/constants";
import { describeRecurrence } from "@dream/domain";
import { Dialog, LateBadge, RecurringBadge, Skeleton, StatusBadge } from "@dream/ui";
import { DateTime } from "luxon";
import type { ReactNode } from "react";
import {
  useClientViewAttachments,
  useClientViewDeliverable,
  usePublicAttachments,
  usePublicDeliverable,
} from "../hooks/use-calendar.ts";
import { chipInkForFill } from "../lib/chip-contrast.ts";
import { TypeIcon } from "./type-icon.tsx";

/**
 * Detalhe da entrega no portal do cliente.
 *
 * Somente leitura. Busca o GET público (não o recorte da ocorrência no
 * calendário) para o cliente ver título, tipo, projeto/campanha, prazos,
 * descrição, publicação, links e arquivos públicos — tudo o que a equipe
 * marcou como visível.
 */

export interface PortalDeliverableDialogProps {
  slug?: string;
  token?: string;
  handle?: string;
  password?: string;
  deliverableId: string;
  timezone: string;
  clientName: string | null;
  clientColor?: string;
  projects: Array<{ id: string; name: string; color: string }>;
  types: Array<{
    id: string;
    label: string;
    color: string;
    slug?: string;
    icon?: string | null;
  }>;
  onClose: () => void;
}

const CLIENT_STATUS_HINT: Record<DeliverableStatus, string> = {
  planned: "Na fila da equipe",
  in_production: "A Dream Vision está produzindo agora",
  in_review: "Em revisão interna",
  approved: "Aprovado — segue para publicação",
  scheduled: "Agendado para sair",
  published: "Já publicado",
  completed: "Concluído",
  late: "Passou do prazo — a equipe está em cima",
  cancelled: "Esta entrega foi cancelada",
};

function formatPeriod(
  startsAt: string,
  endsAt: string,
  isAllDay: boolean,
  timezone: string,
): { headline: string; detail: string } {
  const start = DateTime.fromISO(startsAt, { zone: timezone }).setLocale("pt-BR");
  const end = DateTime.fromISO(endsAt, { zone: timezone }).setLocale("pt-BR");

  if (isAllDay) {
    const lastDay = end.minus({ milliseconds: 1 });
    const sameDay = start.hasSame(lastDay, "day");
    const headline = sameDay
      ? start.toFormat("cccc, dd 'de' LLLL 'de' yyyy")
      : `${start.toFormat("dd 'de' LLLL")} – ${lastDay.toFormat("dd 'de' LLLL 'de' yyyy")}`;
    return { headline, detail: "Dia inteiro" };
  }

  const sameDay = start.hasSame(end, "day");
  const headline = start.toFormat("cccc, dd 'de' LLLL 'de' yyyy");
  const detail = sameDay
    ? `${start.toFormat("HH:mm")} – ${end.toFormat("HH:mm")}`
    : `${start.toFormat("dd/MM HH:mm")} – ${end.toFormat("dd/MM HH:mm")}`;
  return { headline, detail };
}

export function PortalDeliverableDialog({
  slug,
  token,
  handle,
  password,
  deliverableId,
  timezone,
  clientName,
  clientColor = "#0074FF",
  projects,
  types,
  onClose,
}: PortalDeliverableDialogProps) {
  const shareMode = handle === undefined || handle === "";
  const shareDetail = usePublicDeliverable(
    slug ?? "",
    token ?? "",
    shareMode ? deliverableId : null,
    password,
  );
  const shareAttachments = usePublicAttachments(
    slug ?? "",
    token ?? "",
    shareMode ? deliverableId : null,
    password,
  );
  const clientDetail = useClientViewDeliverable(handle ?? "", shareMode ? null : deliverableId);
  const clientAttachments = useClientViewAttachments(
    handle ?? "",
    shareMode ? null : deliverableId,
  );
  const detail = shareMode ? shareDetail : clientDetail;
  const attachments = shareMode ? shareAttachments : clientAttachments;

  const deliverable = detail.data?.deliverable;
  const recurrence = detail.data?.recurrence ?? null;

  const type =
    deliverable === undefined ? undefined : types.find((item) => item.id === deliverable.typeId);
  const project =
    deliverable === undefined || deliverable.projectId === null
      ? undefined
      : projects.find((item) => item.id === deliverable.projectId);

  const period =
    deliverable === undefined
      ? null
      : formatPeriod(deliverable.startsAt, deliverable.endsAt, deliverable.isAllDay, timezone);

  const recurrenceLabel =
    recurrence === null
      ? null
      : (() => {
          try {
            return describeRecurrence(
              recurrence.rrule,
              recurrence.timezone,
              deliverable === undefined ? new Date() : new Date(deliverable.startsAt),
            );
          } catch {
            return "Entrega recorrente";
          }
        })();

  const title = deliverable?.title ?? "Entrega";
  const description = period?.headline;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
      description={description}
      size="md"
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : detail.error !== null || deliverable === undefined ? (
        <p className="text-[0.875rem] text-[var(--color-ink-muted)]">
          Esta entrega não está disponível neste acompanhamento.
        </p>
      ) : (
        <div className="space-y-4 sm:space-y-5">
          <div
            className="-mx-4 -mt-3 h-1.5 sm:-mx-5 sm:-mt-4"
            style={{ backgroundColor: type?.color ?? clientColor }}
            aria-hidden="true"
          />

          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={deliverable.status as DeliverableStatus} size="md" />
            {deliverable.status === "late" && <LateBadge />}
            {recurrenceLabel !== null && <RecurringBadge />}
          </div>
          <p className="text-[0.875rem] leading-5 text-[var(--color-ink-secondary)]">
            {CLIENT_STATUS_HINT[deliverable.status as DeliverableStatus]}
          </p>

          <dl className="grid gap-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-4">
            {period !== null && (
              <Detail term="Quando">
                <span className="block text-[1rem] font-semibold leading-6">{period.headline}</span>
                <span className="block text-[var(--color-ink-muted)]">{period.detail}</span>
              </Detail>
            )}

            {type !== undefined && (
              <Detail term="Tipo">
                <span className="inline-flex items-center gap-2">
                  <span
                    className="flex size-7 shrink-0 items-center justify-center rounded-md"
                    style={{ backgroundColor: type.color, color: chipInkForFill(type.color).ink }}
                    aria-hidden="true"
                  >
                    <TypeIcon slug={type.slug} name={type.icon} size={14} />
                  </span>
                  {type.label}
                </span>
              </Detail>
            )}

            {clientName !== null && <Detail term="Cliente">{clientName}</Detail>}

            {project !== undefined && (
              <Detail term="Projeto ou campanha">
                <span className="inline-flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  {project.name}
                </span>
              </Detail>
            )}

            {deliverable.publishAt !== null && (
              <Detail term="Publicação prevista">
                {DateTime.fromISO(deliverable.publishAt, { zone: timezone })
                  .setLocale("pt-BR")
                  .toFormat("dd/MM/yyyy 'às' HH:mm")}
              </Detail>
            )}

            {recurrenceLabel !== null && <Detail term="Repetição">{recurrenceLabel}</Detail>}
          </dl>

          {deliverable.description !== null && deliverable.description !== "" && (
            <section>
              <h3 className="mb-1.5 text-[0.75rem] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                Detalhes
              </h3>
              <p className="whitespace-pre-line text-[0.875rem] leading-6 text-[var(--color-ink-secondary)]">
                {deliverable.description}
              </p>
            </section>
          )}

          {deliverable.links.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-[0.75rem] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                Links
              </h3>
              <ul className="space-y-1.5">
                {deliverable.links.map((link) => (
                  <li key={link.url}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-[0.875rem] font-medium text-brand-700 underline decoration-brand-300 underline-offset-2 hover:decoration-brand-600"
                    >
                      {link.label === "" ? link.url : link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {attachments.data !== undefined && attachments.data.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-[0.75rem] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                Arquivos
              </h3>
              <ul className="space-y-1 text-[0.8125rem] text-[var(--color-ink-secondary)]">
                {attachments.data.map((file) => (
                  <li key={file.id}>{file.fileName}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-[var(--color-ink-subtle)]">
                O download de anexos ainda não está disponível neste acompanhamento. Use os links
                acima quando a equipe os tiver incluído.
              </p>
            </section>
          )}

          <p className="border-t border-[var(--color-line)] pt-4 text-[0.75rem] leading-5 text-[var(--color-ink-muted)]">
            Este calendário é só para acompanhar. Para pedir mudança de data ou de conteúdo, fale
            com a equipe da Dream Vision.
          </p>
        </div>
      )}
    </Dialog>
  );
}

function Detail({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
        {term}
      </dt>
      <dd className="mt-0.5 text-[0.875rem] leading-5 text-[var(--color-ink)]">{children}</dd>
    </div>
  );
}
